import {
  AccountBalanceWalletOutlined,
  CreditCardOutlined,
  GavelOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { PAY_INPUT_CONFIG_ITEMS } from "./PayInputConfiguration/payInputConfigData";

// Payroll Settings: the pages behind the single "Payroll Settings" line at
// the bottom of the Payroll sidebar. On these pages the sidebar swaps the
// Payroll menu for this menu (Sidebar/index.js, "‹ Payroll" swaps back), and
// the Overview page (PayrollSettingsOverview) shows every item as a card.
//
// Each group is a heading with its pages listed flat below it. `match`
// (optional) is the query value that picks the item on a shared page;
// `isDefault` marks the item the page shows when the value is missing.
export const PAYROLL_SETTINGS_BASE_PATH = "/admin/payroll/configuration";

const PAY_INPUT_PATH = `${PAYROLL_SETTINGS_BASE_PATH}/pay-input-configuration`;
const STATUTORY_PATH = `${PAYROLL_SETTINGS_BASE_PATH}/statutory-configuration`;
const PAYROLL_PATH = `${PAYROLL_SETTINGS_BASE_PATH}/payroll-configuration`;

export const PAYROLL_SETTINGS_OVERVIEW = {
  key: "overview",
  label: "Overview",
  path: `${PAYROLL_SETTINGS_BASE_PATH}/overview`,
};

// The day-to-day screen each settings page belongs to (dailyPath / dailyLabel),
// for the switch buttons between the two (Shared/settingsSwitch.js).
const PAY_INPUTS_PATH = "/admin/payroll/pay-inputs";
const STATUTORY_SCREENS_PATH = "/admin/payroll/statutory";
// Pay input pages whose URL differs from their settings key.
const PAY_INPUT_DAILY_SLUGS = { "salary-hold": "salary-holds" };

// Short line under each page's name on the Overview cards.
const PAY_INPUT_DESCRIPTIONS = {
  reimbursements: "Types, limits and approval",
};

export const PAYROLL_SETTINGS_MENU = [
  {
    key: "pay-input",
    label: "Pay Input",
    icon: CreditCardOutlined,
    tint: { color: "#EA580C", background: "#FFF1E7" },
    items: PAY_INPUT_CONFIG_ITEMS.map(({ key, label }, index) => ({
      key,
      label,
      description: PAY_INPUT_DESCRIPTIONS[key] || "Treatment, approval and sources",
      path: `${PAY_INPUT_PATH}/${key}`,
      isDefault: index === 0,
      dailyPath: `${PAY_INPUTS_PATH}/${PAY_INPUT_DAILY_SLUGS[key] || key}`,
      dailyLabel: label,
    })),
  },
  {
    key: "statutory",
    label: "Statutory",
    icon: GavelOutlined,
    tint: { color: "#0F766E", background: "#E6F8F4" },
    items: [
      { key: "pf", label: "Provident Fund (PF)", description: "Rates, ceiling and codes", dailyLabel: "PF Members", isDefault: true },
      { key: "esi", label: "ESI", description: "Rates, codes and reason codes", dailyLabel: "ESI Eligibility" },
      { key: "pt", label: "Professional Tax", description: "Site-wise slabs", dailyLabel: "PT Employees" },
      { key: "lwf", label: "Labour Welfare Fund", description: "Site-wise rates", dailyLabel: "LWF Employees" },
    ].map((item) => ({
      ...item,
      path: STATUTORY_PATH,
      match: { param: "item", value: item.key },
      dailyPath: `${STATUTORY_SCREENS_PATH}/${item.key}`,
    })),
  },
  {
    key: "payroll",
    label: "Payroll",
    icon: TuneOutlined,
    tint: { color: "#7C3AED", background: "#F3EEFE" },
    items: [
      { key: "payment-bank", label: "Payment Bank", description: "Banks salaries are paid from", isDefault: true },
      { key: "payrun-cycle", label: "Payrun Cycle", description: "Month, cut-off and pay date" },
      { key: "payroll-group", label: "Payroll Group", description: "Who is paid together" },
      { key: "rounding-rules", label: "Rounding Rules", description: "How amounts are rounded" },
      { key: "pay-slip", label: "Pay Slip", description: "Payslip layout and content" },
      { key: "daily-calculation-basis", label: "Daily Calculation Basis", description: "How a day's pay is worked out" },
    ].map((item) => ({
      ...item,
      path: PAYROLL_PATH,
      match: { param: "view", value: item.key },
    })),
  },
  {
    key: "disbursement",
    label: "Disbursement",
    icon: AccountBalanceWalletOutlined,
    tint: { color: "#DB2777", background: "#FDECF4" },
    items: [
      {
        key: "disbursement",
        label: "Disbursement",
        description: "How salaries are paid out",
        path: `${PAYROLL_SETTINGS_BASE_PATH}/disbursement-configuration`,
      },
    ],
  },
];

// Breadcrumb trail for a Payroll Settings page: "Payroll › Payroll Settings
// › …rest". Every crumb but the last is a link:
// - "Payroll" opens the first Payroll page (Salary › Salary Components);
// - "Payroll Settings" opens Overview;
// - a group name (Pay Input, Statutory, Payroll) opens Overview at that group.
// The breadcrumb never links the last crumb (the page on screen).
export const PAYROLL_HOME_PATH = "/admin/payroll/salary/salary-components";
export const settingsGroupAnchor = (groupKey) => `settings-group-${groupKey}`;

export const usePayrollSettingsCrumbs = () => {
  const history = useHistory();
  const link = (label, to) => ({ label, onClick: () => history.push(to) });
  return (...rest) => [
    link("Payroll", PAYROLL_HOME_PATH),
    link("Payroll Settings", PAYROLL_SETTINGS_OVERVIEW.path),
    ...rest.map((item) => {
      const group =
        typeof item === "string" &&
        PAYROLL_SETTINGS_MENU.find((entry) => entry.label === item);
      return group
        ? link(item, `${PAYROLL_SETTINGS_OVERVIEW.path}#${settingsGroupAnchor(group.key)}`)
        : item;
    }),
  ];
};

// The settings item for a group + key, e.g. ("pay-input", "overtime").
export const findSettingsItem = (groupKey, itemKey) =>
  PAYROLL_SETTINGS_MENU.find((group) => group.key === groupKey)?.items.find(
    (item) => item.key === itemKey,
  ) || null;

// The link for a menu item: its path plus the query value that picks it.
export const settingsItemHref = (item) =>
  item.match ? `${item.path}?${item.match.param}=${item.match.value}` : item.path;

const trimSlash = (path) => (path || "").replace(/\/+$/, "");

// Whether `pathname` is a Payroll Settings page.
export const isPayrollSettingsPath = (pathname) => {
  const current = trimSlash(pathname);
  return (
    current === PAYROLL_SETTINGS_BASE_PATH ||
    current.startsWith(`${PAYROLL_SETTINGS_BASE_PATH}/`)
  );
};

// The group holding the page on screen, if any.
export const findActiveSettingsGroup = (location) =>
  PAYROLL_SETTINGS_MENU.find((group) =>
    group.items.some((item) => isSettingsItemActive(item, location)),
  )?.key || null;

// Whether `item` is the page on screen.
export const isSettingsItemActive = (item, { pathname, search }) => {
  const current = trimSlash(pathname);
  if (!item.match) {
    return current === item.path || current.startsWith(`${item.path}/`);
  }
  if (current !== item.path) return false;
  const value = new URLSearchParams(search).get(item.match.param);
  return value ? value === item.match.value : Boolean(item.isDefault);
};
